import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import {
    getMySubcontractors,
    assignSubcontractor,
    getSubcontractPreview,
    sendSubcontractContract,
    getMissions
} from '../../services/missionService';
import { toast, ToastContainer } from 'react-toastify';
import {
    ChevronRight,
    ChevronLeft,
    Truck,
    FileText,
    DollarSign,
    CheckCircle2,
    Building2,
    Plus,
    Trash2,
    Info,
    Mail,
    Send,
    Loader2
} from 'lucide-react';

import { useI18n } from '../../i18n/I18nContext';
import './MiniTimeline.css';

const SubcontractMission = () => {
    const { t } = useI18n();
    const location = useLocation();
    const navigate = useNavigate();
    const queryParams = new URLSearchParams(location.search);
    const missionIdFromUrl = queryParams.get('missionId');

    const [step, setStep] = useState(1);
    const [loading, setLoading] = useState(false);
    const [missions, setMissions] = useState([]);
    const [subcontractors, setSubcontractors] = useState([]);
    const [previewHtml, setPreviewHtml] = useState('');

    // Form State
    const [selectedMission, setSelectedMission] = useState(null);
    const [selectedSub, setSelectedSub] = useState(null);
    const [instructions, setInstructions] = useState('');
    const [vehicleRequested, setVehicleRequested] = useState('');
    const [currency, setCurrency] = useState('EUR');
    const [missionPrice, setMissionPrice] = useState(0);
    const [priceLines, setPriceLines] = useState([{ description: 'Transport de base', amount: 0 }]);

    useEffect(() => {
        loadInitialData();
    }, []);

    const loadInitialData = async () => {
        try {
            setLoading(true);
            const [subsData, missionsData] = await Promise.all([
                getMySubcontractors(),
                getMissions()
            ]);
            setSubcontractors(subsData);

            // Filter missions: must be assigned to current user and ideally not yet subcontracted
            // For now, allow selecting any mission assigned to user
            setMissions(missionsData.filter(m => m.status !== 'TERMINEE'));

            if (missionIdFromUrl) {
                const m = missionsData.find(x => x.id === missionIdFromUrl);
                if (m) {
                    setSelectedMission(m);
                    setMissionPrice(m.price || 0);
                    setVehicleRequested(m.vehicleType || '');
                    setPriceLines([{ description: 'Transport de base', amount: m.price || 0 }]);
                    setStep(2); // Jump to subcontractor selection
                }
            }
        } catch (err) {
            toast.error(t('common.errorLoadingPositions')); // Or another generic error
        } finally {
            setLoading(false);
        }
    };

    const handleAddPriceLine = () => {
        setPriceLines([...priceLines, { description: '', amount: 0 }]);
    };

    const handleRemovePriceLine = (index) => {
        setPriceLines(priceLines.filter((_, i) => i !== index));
    };

    const handlePriceLineChange = (index, field, value) => {
        const newLines = [...priceLines];
        newLines[index][field] = field === 'amount' ? parseFloat(value) || 0 : value;
        setPriceLines(newLines);
    };

    const totalSubcontractCost = priceLines.reduce((sum, line) => sum + line.amount, 0);

    const handleGeneratePreview = async () => {
        if (!selectedMission || !selectedSub) {
            toast.warning(t('subcontractForm.errors.selectMissionSub'));
            return;
        }
        try {
            setLoading(true);
            // Before preview, we save the details (instructions, costs)
            const payload = {
                missionId: selectedMission.id,
                subcontractorId: selectedSub.id,
                instructions,
                vehicleRequested,
                missionPrice,
                subcontractingCost: totalSubcontractCost,
                currency,
                priceLines
            };

            // We call assign but the backend will just update/create the Subcontract entity
            await assignSubcontractor(payload);

            // Now get preview
            const preview = await getSubcontractPreview(selectedMission.id, selectedSub.id, currency);
            setPreviewHtml(preview.html);
            setStep(4);
        } catch (err) {
            toast.error(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleFinalConfirm = async () => {
        try {
            setLoading(true);
            await sendSubcontractContract(selectedMission.id, selectedSub.id, currency);
            toast.success(t('subcontractForm.success.sent'));
            setStep(5);
        } catch (err) {
            toast.error(err.message);
        } finally {
            setLoading(false);
        }
    };

    const renderMiniTimeline = (mission) => {
        const steps = [
            { label: t('missions.status.timeline.departure') || 'Départ', done: true },
            { label: t('missions.status.timeline.arrivalLoading') || 'Arrivée chargement', done: !!mission.loadingArrivalTime },
            { label: t('missions.status.timeline.departureLoading') || 'Départ chargement', done: !!mission.loadingDepartureTime },
            { label: t('missions.status.timeline.arrivalDelivery') || 'Arrivée livraison', done: !!mission.deliveryArrivalTime },
            { label: t('missions.status.timeline.completed') || 'Terminée', done: mission.status === 'TERMINEE' || mission.status === 'ANNULEE' }
        ];

        return (
            <div className="mini-timeline" style={{ width: '80px' }}>
                {steps.map((step, idx) => {
                    const isNext = !step.done && (idx === 0 || steps[idx - 1].done);
                    return (
                        <div
                            key={idx}
                            className={`mini-step ${step.done ? 'done' : ''} ${isNext ? 'active' : ''}`}
                            data-label={step.label}
                        >
                            <div className="mini-dot" style={{ width: '6px', height: '6px' }}></div>
                            {idx < steps.length - 1 && <div className="mini-line"></div>}
                        </div>
                    );
                })}
            </div>
        );
    };

    const renderStep1 = () => (
        <div className="card border-0 shadow-sm rounded-4 p-4">
            <h5 className="fw-bold mb-4">{t('subcontractForm.steps.step1.title')}</h5>
            <div className="row g-3">
                {missions.length > 0 ? missions.map(m => (
                    <div key={m.id} className="col-12">
                        <div
                            className={`p-3 border rounded-4 cursor-pointer hover-bg-light transition-all d-flex justify-content-between align-items-center ${selectedMission?.id === m.id ? 'border-primary bg-primary bg-opacity-10' : ''}`}
                            onClick={() => {
                                setSelectedMission(m);
                                setMissionPrice(m.price || 0);
                                setVehicleRequested(m.vehicleType || '');
                                setPriceLines([{ description: t('subcontractForm.baseTransport'), amount: m.price || 0 }]);
                            }}
                        >
                            <div>
                                <div className="fw-bold">{m.departureLocation} → {m.arrivalLocation}</div>
                                <div className="small text-muted">Réf: {m.referenceNumber || m.id} | {m.cargoType}</div>
                                <div className="mt-2">
                                    {renderMiniTimeline(m)}
                                </div>
                            </div>
                            <div className="text-end">
                                <div className="fw-bold text-primary">{m.price} {m.currency || 'EUR'}</div>
                                <div className="small text-muted">{new Date(m.departureDate).toLocaleDateString()}</div>
                            </div>
                        </div>
                    </div>
                )) : <div className="text-center py-5 text-muted">{t('subcontractForm.step1.empty')}</div>}
            </div>
            <div className="mt-4 d-flex justify-content-end">
                <button
                    className="btn btn-primary rounded-pill px-4 d-flex align-items-center gap-2"
                    disabled={!selectedMission}
                    onClick={() => setStep(2)}
                >
                    {t('common.continue')} <ChevronRight size={18} />
                </button>
            </div>
        </div>
    );

    const renderStep2 = () => (
        <div className="card border-0 shadow-sm rounded-4 p-4">
            <h5 className="fw-bold mb-4">{t('subcontractForm.steps.step2.title')}</h5>
            <div className="row g-3">
                {subcontractors.length > 0 ? subcontractors.map(s => (
                    <div key={s.id} className="col-12 col-md-6">
                        <div
                            className={`p-3 border rounded-4 h-100 cursor-pointer hover-bg-light transition-all d-flex justify-content-between align-items-center ${selectedSub?.id === s.id ? 'border-primary bg-primary bg-opacity-10' : ''}`}
                            onClick={() => setSelectedSub(s)}
                        >
                            <div>
                                <div className="fw-bold">{s.enterpriseName}</div>
                                <div className="small text-muted">{s.city}, {s.country}</div>
                                <span className={`badge rounded-pill mt-2 ${s.subcontractorType === 'TMS_ACCOUNT' ? 'bg-info bg-opacity-10 text-info' : 'bg-secondary bg-opacity-10 text-secondary'}`}>
                                    {s.subcontractorType === 'TMS_ACCOUNT' ? 'Compte TMS' : 'Saisie Manuelle'}
                                </span>
                            </div>
                            {selectedSub?.id === s.id && <CheckCircle2 size={24} className="text-primary" />}
                        </div>
                    </div>
                )) : (
                    <div className="col-12 text-center py-5">
                        <p className="text-muted">{t('subcontractForm.step2.empty')}</p>
                        <button className="btn btn-outline-primary" onClick={() => navigate('/subcontractors')}>{t('subcontractForm.step2.manage')}</button>
                    </div>
                )}
            </div>
            <div className="mt-4 d-flex justify-content-between">
                <button className="btn btn-light rounded-pill px-4" onClick={() => setStep(1)}><ChevronLeft size={18} /> {t('common.back')}</button>
                <button
                    className="btn btn-primary rounded-pill px-4 d-flex align-items-center gap-2"
                    disabled={!selectedSub}
                    onClick={() => setStep(3)}
                >
                    {t('subcontractForm.step2.next')} <ChevronRight size={18} />
                </button>
            </div>
        </div>
    );

    const renderStep3 = () => (
        <div className="row g-4">
            <div className="col-12 col-lg-7">
                <div className="card border-0 shadow-sm rounded-4 p-4 mb-4">
                    <h5 className="fw-bold mb-4 d-flex align-items-center gap-2">
                        <Info className="text-primary" /> {t('subcontractForm.step3.instructionsTitle')}
                    </h5>
                    <div className="mb-4">
                        <label className="form-label small fw-bold">{t('subcontractForm.step3.instructionsLabel')}</label>
                        <textarea
                            className="form-control bg-light border-0 rounded-3"
                            rows="4"
                            placeholder={t('subcontractForm.step3.instructionsPlaceholder')}
                            value={instructions}
                            onChange={e => setInstructions(e.target.value)}
                        ></textarea>
                    </div>
                    <div>
                        <label className="form-label small fw-bold">{t('subcontractForm.step3.vehicleLabel')}</label>
                        <div className="input-group bg-light rounded-3 overflow-hidden">
                            <span className="input-group-text border-0 bg-transparent text-muted"><Truck size={18} /></span>
                            <input
                                className="form-control border-0 bg-transparent p-2"
                                placeholder={t('subcontractForm.step3.vehiclePlaceholder')}
                                value={vehicleRequested}
                                onChange={e => setVehicleRequested(e.target.value)}
                            />
                        </div>
                    </div>
                </div>

                <div className="card border-0 shadow-sm rounded-4 p-4">
                    <div className="d-flex justify-content-between align-items-center mb-4">
                        <h5 className="fw-bold mb-0 d-flex align-items-center gap-2">
                            <DollarSign className="text-success" /> {t('subcontractForm.step3.priceLinesTitle')}
                        </h5>
                        <button className="btn btn-sm btn-outline-primary rounded-pill px-3 d-flex align-items-center gap-1" onClick={handleAddPriceLine}>
                            <Plus size={14} /> {t('subcontractForm.step3.addLine')}
                        </button>
                    </div>
                    <div className="table-responsive">
                        <table className="table table-borderless align-middle">
                            <thead>
                                <tr className="text-muted small">
                                    <th style={{ width: '60%' }}>{t('subcontractForm.step3.table.description')}</th>
                                    <th style={{ width: '30%' }}>{t('subcontractForm.step3.table.amount')} ({currency})</th>
                                    <th style={{ width: '10%' }}></th>
                                </tr>
                            </thead>
                            <tbody>
                                {priceLines.map((line, idx) => (
                                    <tr key={idx}>
                                        <td>
                                            <input
                                                className="form-control form-control-sm bg-light border-0"
                                                placeholder={t('subcontractForm.step3.table.descriptionPlaceholder')}
                                                value={line.description}
                                                onChange={e => handlePriceLineChange(idx, 'description', e.target.value)}
                                            />
                                        </td>
                                        <td>
                                            <input
                                                type="number"
                                                className="form-control form-control-sm bg-light border-0"
                                                value={line.amount}
                                                onChange={e => handlePriceLineChange(idx, 'amount', e.target.value)}
                                            />
                                        </td>
                                        <td className="text-end">
                                            {priceLines.length > 1 && (
                                                <button className="btn btn-link text-danger p-1" onClick={() => handleRemovePriceLine(idx)}>
                                                    <Trash2 size={16} />
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            <div className="col-12 col-lg-5">
                <div className="card border-0 shadow-sm rounded-4 p-4 mb-4 bg-primary text-white">
                    <h5 className="fw-bold mb-4">{t('subcontractForm.step3.summary.title')}</h5>
                    <div className="vstack gap-3">
                        <div className="d-flex justify-content-between align-items-center">
                            <span className="opacity-75">{t('subcontractForm.step3.summary.salePrice')}</span>
                            <div className="d-flex gap-2 align-items-center">
                                <input
                                    type="number"
                                    className="form-control form-control-sm bg-white bg-opacity-10 border-white border-opacity-25 text-white w-auto text-end"
                                    value={missionPrice}
                                    onChange={e => setMissionPrice(parseFloat(e.target.value) || 0)}
                                />
                                <span className="fw-bold">{currency}</span>
                            </div>
                        </div>
                        <div className="d-flex justify-content-between align-items-center">
                            <span className="opacity-75">{t('subcontractForm.step3.summary.cost')}</span>
                            <span className="fw-bold fs-5">{totalSubcontractCost.toLocaleString()} {currency}</span>
                        </div>
                        <hr className="bg-white my-1" />
                        <div className="d-flex justify-content-between align-items-center">
                            <span className="fw-bold">{t('subcontractForm.step3.summary.margin')}</span>
                            <span className={`fw-bold fs-4 ${missionPrice - totalSubcontractCost >= 0 ? 'text-white' : 'text-danger'}`}>
                                {(missionPrice - totalSubcontractCost).toLocaleString()} {currency}
                            </span>
                        </div>
                    </div>
                </div>

                <div className="card border-0 shadow-sm rounded-4 p-4">
                    <h6 className="fw-bold mb-3">{t('subcontractForm.step3.summary.currency')}</h6>
                    <div className="mb-4">
                        <label className="form-label small text-muted">{t('subcontractForm.step3.summary.currency')}</label>
                        <select className="form-select bg-light border-0" value={currency} onChange={e => setCurrency(e.target.value)}>
                            <option value="EUR">Euro (EUR)</option>
                            <option value="MAD">Dirham Marocain (MAD)</option>
                            <option value="USD">Dollar (USD)</option>
                        </select>
                    </div>

                    <div className="alert alert-info border-0 rounded-4 d-flex gap-3 mb-0">
                        <Info className="flex-shrink-0" />
                        <div className="small">
                            {t('subcontractForm.step3.warning')}
                        </div>
                    </div>
                </div>

                <div className="mt-4 d-flex gap-3">
                    <button className="btn btn-light rounded-pill px-4 flex-grow-1" onClick={() => setStep(2)}>{t('common.back')}</button>
                    <button
                        className="btn btn-primary rounded-pill px-4 flex-grow-2"
                        disabled={loading || totalSubcontractCost === 0}
                        onClick={handleGeneratePreview}
                    >
                        {loading ? <Loader2 className="animate-spin" /> : t('subcontractForm.step3.generate')}
                    </button>
                </div>
            </div>
        </div>
    );

    const renderStep4 = () => (
        <div className="card border-0 shadow-sm rounded-4 overflow-hidden">
            <div className="bg-dark p-3 text-white d-flex justify-content-between align-items-center">
                <span className="fw-bold d-flex align-items-center gap-2"><FileText size={18} /> {t('subcontractForm.step4.title')}</span>
                <div className="badge bg-primary px-3 py-2 rounded-pill">{t('subcontractForm.step4.draft')}</div>
            </div>
            <div className="card-body p-0 border-bottom" style={{ height: '37.5rem', overflowY: 'auto', backgroundColor: '#f0f2f5' }}>
                <div className="bg-white mx-auto my-4 shadow-sm" style={{ width: '210mm', minHeight: '297mm', padding: '20mm' }}>
                    <div dangerouslySetInnerHTML={{ __html: previewHtml }}></div>
                </div>
            </div>
            <div className="card-footer p-4 bg-light border-0 d-flex justify-content-between">
                <button className="btn btn-light rounded-pill px-4" onClick={() => setStep(3)}>{t('subcontractForm.step4.edit')}</button>
                <button
                    className="btn btn-success rounded-pill px-5 d-flex align-items-center gap-2 shadow-sm"
                    disabled={loading}
                    onClick={handleFinalConfirm}
                >
                    {loading ? <Loader2 className="animate-spin" size={20} /> : <><Send size={20} /> {t('subcontractForm.step4.confirm')}</>}
                </button>
            </div>
        </div>
    );

    const renderStep5 = () => (
        <div className="text-center py-5">
            <div className="mb-4">
                <div className="display-1 text-success mb-3"><CheckCircle2 size={120} className="mx-auto" /></div>
                <h2 className="fw-bold">{t('subcontractForm.step5.title')}</h2>
                <p className="text-muted fs-5">{t('subcontractForm.step5.success', { name: selectedSub?.enterpriseName })}</p>
            </div>
            <div className="d-flex gap-3 justify-content-center">
                <button className="btn btn-primary px-4 rounded-pill" onClick={() => navigate('/missions')}>{t('subcontractForm.step5.backToMissions')}</button>
                <button className="btn btn-outline-secondary px-4 rounded-pill" onClick={() => navigate('/dashboard')}>{t('common.dashboard')}</button>
            </div>
        </div>
    );

    return (
        <DashboardLayout>
            <ToastContainer />
            <div className="container py-4">
                <div className="mb-4 text-center">
                    <div className="d-flex justify-content-center align-items-center gap-2 mb-2">
                        {[1, 2, 3, 4, 5].map(s => (
                            <React.Fragment key={s}>
                                <div
                                    className={`rounded-circle d-flex align-items-center justify-content-center fw-bold transition-all shadow-sm ${step >= s ? 'bg-primary text-white' : 'bg-white text-muted border'}`}
                                    style={{ width: '2.5rem', height: '2.5rem' }}
                                >
                                    {step > s ? <CheckCircle2 size={24} /> : s}
                                </div>
                                {s < 5 && <div className={`flex-grow-0 bg-light rounded`} style={{ width: '2.5rem', height: '0.25rem', backgroundColor: step > s ? '#0d6efd' : '#e9ecef' }}></div>}
                            </React.Fragment>
                        ))}
                    </div>
                    <div className="small text-muted fw-bold">
                        {step === 1 && t('subcontractForm.steps.step1.label')}
                        {step === 2 && t('subcontractForm.steps.step2.label')}
                        {step === 3 && t('subcontractForm.steps.step3.label')}
                        {step === 4 && t('subcontractForm.steps.step4.label')}
                        {step === 5 && t('subcontractForm.steps.step5.label')}
                    </div>
                </div>

                {loading && step < 4 && <div className="text-center p-5"><Loader2 className="animate-spin mx-auto text-primary" size={48} /></div>}

                {!loading && step === 1 && renderStep1()}
                {!loading && step === 2 && renderStep2()}
                {!loading && step === 3 && renderStep3()}
                {step === 4 && renderStep4()}
                {step === 5 && renderStep5()}
            </div>

            <style>{`
                .cursor-pointer { cursor: pointer; }
                .hover-bg-light:hover { background-color: #f8f9fa !important; }
                .transition-all { transition: all 0.2s ease; }
                .flex-grow-2 { flex-grow: 2; }
                .animate-spin { animation: spin 1s linear infinite; }
                @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
            `}</style>
        </DashboardLayout>
    );
};

export default SubcontractMission;
